// Provenance: repo:tds-reconciliation-portal:frontend/src/components/CompanyWorkbench.tsx
// (ADR-0005) — restyled on the estate tokens (ADR-0010). Ids became UUID strings; the
// fingerprint-keyed selection, band grouping, wire caps and contested flow carry unchanged.
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { ChevronDown, ChevronRight, ExternalLink, Search } from 'lucide-react'
import { useState } from 'react'
import { Link, useParams } from 'react-router'

import { ApiError, postJson } from '../api/client'
import { useCompanyOffers, useDiffCompanies } from '../api/hooks'
import { openForDays } from '../api/reconChips'
import {
  ACCEPT_BATCH_MAX, chunked, contestedSelection, copiesOf, nothingToOfferText,
  OFFER_FP_MAX, offerGroups, sendable,
} from '../api/txnMatch'
import type {
  CompanyOffers, ContestedReceipt, DiffCompany, Offer, OfferMember,
} from '../api/types'
import { matchedChip, needsYouChip } from '../api/worklistChips'
import { fmtDate } from '../format'
import { useDebounced } from '../lib/useDebounced'
import { Chip, type Tone } from './badges'
import { InfoTip } from './InfoTip'
import { Btn } from './Modal'
import { Money } from './Money'
import { showToast } from './toast'
import { TxnMatcher } from './TxnMatcher'

const fmt = new Intl.NumberFormat('en-IN')

/** An offer's identity for selection. Its fingerprints ARE what it proposes, so
 *  two offers cannot collide and a refetch cannot move a tick onto a different
 *  suggestion the way a list index would. */
const keyOf = (o: Offer) => o.fingerprints.join('|')

const th = 'px-2 py-1.5 text-left text-xs font-medium uppercase text-muted-foreground'

/** The amounts, each with its own date INLINE — `1,000 (12 Apr) + 2,000
 *  (14 Apr)` — so a stray entry inside a set is still visible (the date
 *  rides its amount, not a range) while the row stays ONE line. */
function Members({ members }: { members: OfferMember[] }) {
  return (
    <span className="text-xs">
      {members.map((m, i) => (
        <span key={i} className="whitespace-nowrap">
          {i > 0 ? ' + ' : ''}
          {m.tds}
          <span className="text-[10px] text-muted-foreground">
            {' '}({m.date ? fmtDate(m.date, { year: false }) : 'no date'})
          </span>
          {/* A grouped member is ONE line here and several entries in the
              reconciliation. Accepting claims every one of them, so the
              count is stated on the line rather than left to the badge —
              "dup" alone would read as a warning about a single entry. */}
          {(m.copies ?? 1) > 1 ? (
            <Chip tone="orange" className="ml-1">{m.copies} identical</Chip>
          ) : m.duplicate && (
            <Chip tone="orange" className="ml-1">dup</Chip>
          )}
        </span>
      ))}
    </span>
  )
}

// The dates here are BUSINESS dates — the day a deduction was made — so they
// go through `fmtDate`, which never constructs a Date.

function OfferRow({
  offer,
  onAccept,
  onReject,
  busy,
  blocked,
  checked,
  onCheck,
}: {
  offer: Offer
  onAccept: () => void
  onReject: () => void
  busy: boolean
  /** cannot go on the wire — more fingerprints than one claim may carry.
   *  No checkbox and no buttons: accept AND reject both take the same capped
   *  list, so offering either would fail after the click. */
  blocked?: boolean
  checked: boolean
  onCheck: () => void
}) {
  const ev = offer.evidence
  return (
    <tr className="border-t border-border">
      <td className="w-8 px-2 py-1">
        {!blocked && (
          <input type="checkbox" checked={checked} onChange={onCheck} disabled={busy}
            aria-label="Select this suggestion" />
        )}
      </td>
      <td className="px-2 py-1"><Chip tone="blue">{offer.cardinality}</Chip></td>
      <td className="px-2 py-1"><Members members={offer.members_26as} /></td>
      <td className="px-2 py-1"><Members members={offer.members_books} /></td>
      <td className="px-2 py-1 text-right"><Money v={offer.tds} bold className="text-xs" /></td>
      <td className="px-2 py-1">
        {ev && (
          <span className="text-[10px] text-muted-foreground">
            {ev.members} entries
            {ev.max_days != null ? ` · ${ev.max_days}d apart` : ' · undated'}
            {ev.interchangeable ? ' · equivalent entries exist' : ''}
          </span>
        )}
      </td>
      <td className="px-2 py-1">
        {blocked ? (
          <span className="text-xs text-muted-foreground">match by hand</span>
        ) : (
          <span className="flex flex-nowrap gap-1">
            <Btn size="xs" disabled={busy} onClick={onAccept}
              className="border-emerald-300 text-emerald-700 hover:bg-emerald-50">
              Accept
            </Btn>
            <Btn size="xs" kind="subtle" disabled={busy} onClick={onReject}>
              Not this
            </Btn>
          </span>
        )}
      </td>
    </tr>
  )
}

function Contested({ runId, items, busy, invalidate }: {
  runId: string
  items: ContestedReceipt[]
  busy: boolean
  invalidate: () => void
}) {
  // the refusal is right — the machine must not choose between two sets that
  // both tie. This is the missing half: the human's one-click decision the
  // refusal exists to set up, through the same manual-match door every
  // hand-built group goes through. On success the receipt leaves the list by
  // construction: contested receipts are derived from open rows.
  const take = useMutation({
    mutationFn: (p: { fingerprints: string[]; expect_copies?: Record<string, number> }) =>
      postJson(`/runs/${runId}/difference/manual-match`, p),
    onSuccess: () => {
      invalidate()
      showToast({ color: 'green', title: 'Matched — the receipt leaves the contested list.' })
    },
    onError: (e) =>
      showToast({
        color: 'red',
        title: 'Could not take this set',
        message: e instanceof ApiError ? e.message : String(e),
      }),
  })
  if (!items.length) return null
  return (
    <div className="mt-2 rounded-md border border-violet-300 bg-violet-50 p-3">
      <div className="mb-2 flex items-center gap-1">
        <p className="text-sm font-semibold text-foreground">
          {items.length} receipt{items.length === 1 ? '' : 's'} the engine will
          not choose for
        </p>
        {/* the why reads once — behind the ⓘ, not on every card */}
        <InfoTip w={380}>
          More than one set of entries sums to each of these, and they differ —
          so picking one would be the software guessing. The document number or
          the dates usually settle it; when they have, take that set.
        </InfoTip>
      </div>
      <div className="space-y-1.5">
        {items.slice(0, 12).map((c) => (
          <div key={c.target.fingerprint} className="rounded-md border border-border bg-background p-2.5">
            <p className="text-xs font-semibold text-foreground">
              Receipt <Money v={c.target.tds} bold className="text-xs" />
              {c.target.date ? ` · ${c.target.date}` : ''}
            </p>
            {c.options.map((opt, i) => (
              <div key={i} className="mt-0.5 flex flex-nowrap items-center gap-2">
                <p className="flex-1 text-xs text-muted-foreground">
                  {String.fromCharCode(65 + i)}:{' '}
                  {opt.map((m) => `${m.tds}${m.date ? ` (${m.date})` : ''}`).join('  +  ')}
                </p>
                <Btn
                  size="xs"
                  disabled={busy || take.isPending}
                  onClick={() => {
                    const p = contestedSelection(c, i)
                    if (p) take.mutate(p)
                  }}
                  className="border-violet-300 text-violet-700 hover:bg-violet-100"
                >
                  Take this set
                </Btn>
              </div>
            ))}
          </div>
        ))}
      </div>
      {items.length > 12 && (
        <p className="mt-1.5 text-xs text-muted-foreground">…and {items.length - 12} more.</p>
      )}
    </div>
  )
}

const BAND_TONE: Record<string, Tone> = {
  strong: 'green', medium: 'blue', weak: 'orange', plain: 'teal', aggregate: 'gray',
}

function CompanyPanel({ runId, company }: { runId: string; company: DiffCompany }) {
  const q = useCompanyOffers(runId, company.party)
  const qc = useQueryClient()
  const [busy, setBusy] = useState(false)
  const [picked, setPicked] = useState<Set<string>>(new Set())
  // Which bands are open. Closed by default beyond the first: a wall of
  // same-amount rows is not a list you can choose from. The header keeps
  // saying how many and how much, so a closed band still states what it holds.
  const [shut, setShut] = useState<Set<string>>(new Set())
  const { clientId, periodId } = useParams()

  const invalidate = () => {
    for (const k of ['company-offers', 'diff-companies', 'difference',
      'difference-summary', 'reconciliation', 'cleared', 'match-suggestions']) {
      void qc.invalidateQueries({ queryKey: [k] })
    }
  }

  const accept = useMutation({
    // The server takes at most ACCEPT_BATCH_MAX offers per request — a DoS
    // guard that stays. Chunks go in sequence and the results are summed, so
    // the reader sees one outcome; a chunk that fails mid-way surfaces with
    // what had already been recorded.
    mutationFn: async (p: { offers: Offer[]; band?: string }) => {
      const out = { accepted: 0, refused: [] as { reason: string }[] }
      for (const part of chunked(p.offers, ACCEPT_BATCH_MAX)) {
        const r = await postJson<{ accepted: number; refused: { reason: string }[] }>(
          `/runs/${runId}/difference/accept-offers`,
          {
            band: p.band ?? null,
            // a member standing for several identical entries is claimed
            // whole, and the server refuses to take copies the caller has not
            // accounted for — so the count goes with the offer it came from
            offers: part.map((o) => ({
              fingerprints: o.fingerprints,
              expect_copies: copiesOf(o),
            })),
          })
        out.accepted += r.accepted
        out.refused.push(...r.refused)
      }
      return out
    },
    onSuccess: (r) => {
      showToast({
        color: r.accepted ? 'green' : 'yellow',
        title: `${r.accepted} match${r.accepted === 1 ? '' : 'es'} recorded`,
        message: r.refused.length
          ? `${r.refused.length} could not be accepted: ${r.refused[0].reason}`
          : 'Anything you did not accept is still open and will be offered again.',
      })
      invalidate()
    },
    onError: (e) => showToast({
      color: 'red', title: 'Could not accept',
      message: e instanceof ApiError ? e.message : String(e),
    }),
  })

  const reject = useMutation({
    mutationFn: (p: { offer: Offer; reason: string }) =>
      postJson(`/runs/${runId}/difference/reject-offer`, {
        fingerprints: p.offer.fingerprints, reason: p.reason,
      }),
    onSuccess: () => {
      showToast({
        color: 'blue',
        title: 'Noted — this will not be offered again. You can undo it from the audit trail.',
      })
      invalidate()
    },
    onError: (e) => showToast({
      color: 'red', title: 'Could not record that',
      message: e instanceof ApiError ? e.message : String(e),
    }),
  })

  if (q.isLoading) return <p className="p-3 text-sm text-muted-foreground">Loading…</p>
  const data: CompanyOffers | undefined = q.data
  if (q.isError) {
    // Returning null here is how "your session expired" rendered as
    // "companies do not expand": the card opened onto nothing, with the 401
    // visible only in the server log. An empty panel and a failed panel are
    // different facts, and the reader gets the one that names the remedy.
    const err = q.error
    const detail = err instanceof ApiError ? err.message : String(err)
    return (
      <div className="m-3 rounded-md border border-red-300 bg-red-50 p-3 text-sm text-red-900">
        Could not load this company&apos;s offers: {detail}
        {err instanceof ApiError && err.status === 401 && (
          <> — your session has expired. <b>Sign in again</b> and reopen it.</>
        )}
      </div>
    )
  }
  if (!data) return null

  // Selection is keyed by the offer's own fingerprints, not by index: the list
  // is refetched after every accept, and an index would silently move to a
  // different suggestion under a tick the reader had already placed.
  const run = async (fn: () => Promise<unknown>) => {
    setBusy(true)
    try { await fn() } finally { setBusy(false) }
  }

  return (
    <div className="space-y-2 px-3 pb-3">
      {/* STATE stays printed (the counts), the explanation folds behind the ⓘ */}
      {(data.search_note || (data.dup_review_needed ?? 0) > 0) && (
        <div className="flex flex-nowrap items-center gap-1.5">
          {data.search_note && (
            <span className="whitespace-nowrap text-xs text-muted-foreground">search limited</span>
          )}
          {(data.dup_review_needed ?? 0) > 0 && (
            <span className="whitespace-nowrap text-xs text-orange-700">
              {data.dup_review_needed} identical repeats
            </span>
          )}
          <InfoTip w={420}>
            {data.search_note ? <>{data.search_note}<br /></> : null}
            Identical repeats share one identity and are offered as whole
            groups — a match claims every copy, which is why an offer says how
            many it stands for. A group can only be taken entire; matching
            some of its copies is not something any verdict can express.
          </InfoTip>
        </div>
      )}
      {!data.suggestions.length && (
        <p className="text-sm text-muted-foreground">
          {nothingToOfferText(data.tolerance_matched)}
        </p>
      )}
      {/* Grouped by what is TRUE of each offer, not by `band ?? 'weak'`:
          a null band means no evidence dict was recorded, which happens only
          for pass-1 same-amount pairs and the leftover aggregate. Filing
          those under Weak showed hundreds of the plainest 1:1 matches beside
          "entries with no date to corroborate them". */}
      {offerGroups(data.suggestions).map((g) => {
        const offers = g.offers
        const total = offers.reduce((a, o) => a + Number(o.tds), 0)
        // What can actually go on the wire: an aggregate offer can carry
        // hundreds of fingerprints against the schema's 64, and sending it
        // rejected the WHOLE request before any offer beside it was read.
        const { ok, blocked } = sendable(offers)
        const blockedKeys = new Set(blocked.map(keyOf))
        const chosen = ok.filter((o) => picked.has(keyOf(o)))
        const chosenTotal = chosen.reduce((a, o) => a + Number(o.tds), 0)
        const tone = BAND_TONE[g.key] ?? (g.color as Tone) ?? 'gray'
        return (
          <div key={g.key} className="rounded-lg border border-border p-2.5">
            <div className="mb-1.5 flex flex-nowrap items-center justify-between gap-2">
              <button
                type="button"
                className="flex flex-nowrap items-center gap-2 text-left"
                onClick={() => setShut((prev) => {
                  const n = new Set(prev)
                  if (n.has(g.key)) n.delete(g.key)
                  else n.add(g.key)
                  return n
                })}
              >
                {shut.has(g.key) ? <ChevronRight size={14} /> : <ChevronDown size={14} />}
                <Chip tone={tone}>{g.label}</Chip>
                <span className="text-xs text-muted-foreground">{g.rule}</span>
              </button>
              <span className="flex flex-nowrap items-center gap-2">
                <span className="whitespace-nowrap text-xs text-muted-foreground">
                  {offers.length} · ₹{fmt.format(Math.round(total))}
                </span>
                {ok.length > 0 && (
                  <Btn size="xs" kind="primary" loading={busy}
                    onClick={() => void run(() =>
                      accept.mutateAsync({ offers: ok, band: g.key }))}>
                    Accept all {ok.length}
                  </Btn>
                )}
              </span>
            </div>

            {/* Between one click for 191 and 191 clicks for 191. Neither of
                those is "the reconciler judged this"; a chosen subset is. The
                total is stated before the button because what you are about to
                dispose of should be visible without counting ticks. */}
            {!shut.has(g.key) && chosen.length > 0 && (
              <div className="mb-1.5 flex flex-nowrap items-center gap-2">
                <span className="text-xs font-semibold text-foreground">
                  {chosen.length} selected · ₹{fmt.format(Math.round(chosenTotal))}
                </span>
                <Btn size="xs" kind="primary" loading={busy}
                  onClick={() => void run(() =>
                    accept.mutateAsync({ offers: chosen, band: g.key })
                      .then(() => setPicked(new Set())))}>
                  Accept selected
                </Btn>
                <Btn size="xs" loading={busy}
                  onClick={() => {
                    const reason = window.prompt(
                      `Why are these ${chosen.length} not one payment? `
                      + '(kept with each decision)')
                    if (!reason || !reason.trim()) return
                    void run(async () => {
                      for (const o of chosen) {
                        await reject.mutateAsync({ offer: o, reason: reason.trim() })
                      }
                      setPicked(new Set())
                    })
                  }}>
                  Not these
                </Btn>
                <Btn size="xs" kind="subtle" onClick={() => setPicked(new Set())}>
                  Clear
                </Btn>
              </div>
            )}
            {!shut.has(g.key) && blocked.length > 0 && (
              <p className="mb-1.5 text-xs text-muted-foreground">
                {blocked.length === 1 && offers.length === 1
                  ? `This offer holds ${fmt.format(blocked[0].fingerprints.length)} entries — `
                  : `${blocked.length} offer(s) here hold too many entries — `}
                more than the {OFFER_FP_MAX} one claim may take. That is not a
                judgement about the money: work it a period at a time with Match
                by hand below.
              </p>
            )}
            {!shut.has(g.key) && (
              <div className="overflow-x-auto">
                <table className="w-full min-w-[640px] text-xs">
                  <thead>
                    <tr>
                      <th className={th} />
                      <th className={th}>Shape</th>
                      <th className={th}>26AS</th>
                      <th className={th}>Books</th>
                      <th className={`${th} text-right`}>Value</th>
                      <th className={th}>Grounds</th>
                      <th className={th} />
                    </tr>
                  </thead>
                  <tbody>
                    {offers.map((o) => (
                      <OfferRow
                        key={o.fingerprints.join('|')}
                        offer={o}
                        busy={busy}
                        blocked={blockedKeys.has(keyOf(o))}
                        checked={picked.has(keyOf(o))}
                        onCheck={() => setPicked((prev) => {
                          const n = new Set(prev)
                          if (n.has(keyOf(o))) n.delete(keyOf(o))
                          else n.add(keyOf(o))
                          return n
                        })}
                        onAccept={() => void run(() =>
                          accept.mutateAsync({ offers: [o], band: g.key }))}
                        onReject={() => {
                          const reason = window.prompt(
                            'Why is this not one payment? (kept with the decision)')
                          if (reason && reason.trim()) {
                            void run(() => reject.mutateAsync({
                              offer: o, reason: reason.trim(),
                            }))
                          }
                        }}
                      />
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )
      })}
      <Contested runId={runId} items={data.ambiguous} busy={busy}
        invalidate={invalidate} />

      {/* Below the offers, in every company — the Name mapping shape: what the
          machine proposes on top, your own judgement underneath. The panel used
          to end with "match by hand on the sheet", sending the reconciler to a
          flat 57,000-row list to do work about ONE company. */}
      <div className="mt-1 rounded-lg border border-border p-2.5">
        <p className="mb-1 text-sm font-semibold text-foreground">Match by hand</p>
        <TxnMatcher
          runId={runId}
          party={company.party}
          n26={data.n_26as}
          nBooks={data.n_books}
        />
      </div>
      <div>
        <Link
          to={`/w/${clientId}/${periodId}/reconciliation?tab=sheet&run=${runId}&party=${encodeURIComponent(company.party)}`}
          className="inline-flex items-center gap-1.5 rounded px-1.5 py-1 text-xs font-medium text-primary hover:bg-muted"
        >
          <ExternalLink size={13} /> See all columns for this company
        </Link>
      </div>
    </div>
  )
}

/** The reconciliation surface, one company at a time and biggest first — the
 *  reconciler's own stated order of work. */
export function CompanyWorkbench({ runId, grain }: { runId: string | null; grain?: string }) {
  const [search, setSearch] = useState('')
  const debounced = useDebounced(search, 300)
  // Amount stays the default: it answers "where is the money", which is why
  // this screen opens on the largest exposure. Name answers "where is ACME",
  // which magnitude order cannot.
  const [sort, setSort] = useState<'amount' | 'name'>('amount')
  const q = useDiffCompanies(runId, debounced, sort)
  const [open, setOpen] = useState<string | null>(null)
  // the machine takes weak (amount-only) matches flagged — this is the
  // one-click way to the companies carrying flags to check
  const [weakOnly, setWeakOnly] = useState(false)

  if (runId == null) return null
  const all = q.data?.items ?? []
  const weakTotal = all.reduce(
    (a, c) => a + (c.needs_parts?.weak_checks ?? 0), 0)
  const items = weakOnly
    ? all.filter((c) => (c.needs_parts?.weak_checks ?? 0) > 0)
    : all
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex w-80 items-center gap-2 rounded-md border border-border bg-background px-2.5 py-1.5">
          <Search size={14} className="shrink-0 text-muted-foreground" />
          <input
            className="w-full bg-transparent text-sm outline-none"
            placeholder="Search any column — party, PAN, TAN, amount, date…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <div className="flex flex-nowrap items-center gap-3">
          {weakTotal > 0 && (
            <button
              type="button"
              title="Machine matches taken on amount alone — worth a look; un-match is one click on the party"
              onClick={() => setWeakOnly((v) => !v)}
            >
              <Chip tone="orange" solid={weakOnly}>{fmt.format(weakTotal)} weak to check</Chip>
            </button>
          )}
          <span className="inline-flex overflow-hidden rounded-md border border-border text-xs">
            {(['amount', 'name'] as const).map((v) => (
              <button
                key={v}
                type="button"
                className={`px-2.5 py-1 ${sort === v ? 'bg-primary text-primary-foreground' : 'hover:bg-muted'}`}
                onClick={() => setSort(v)}
              >
                {v === 'amount' ? 'Amount' : 'A–Z'}
              </button>
            ))}
          </span>
          <span className="text-xs text-muted-foreground">
            {q.data ? `${fmt.format(q.data.total)} companies with work left` : ''}
          </span>
        </div>
      </div>
      {q.isLoading && <p className="text-sm text-muted-foreground">Loading…</p>}
      {q.data && !items.length && (
        <p className="text-sm text-muted-foreground">
          Nothing open{debounced ? ' matching that' : ''}.
        </p>
      )}
      <div className="space-y-1">
        {items.map((c) => {
          const isOpen = open === c.party
          const need = needsYouChip(c)
          return (
            <div key={c.party} className="rounded-lg border border-border">
              <button
                type="button"
                className="flex w-full flex-nowrap items-center justify-between gap-2 px-3 py-1.5 text-left"
                onClick={() => setOpen(isOpen ? null : c.party)}
              >
                <span className="flex min-w-0 flex-nowrap items-center gap-2">
                  {isOpen ? <ChevronDown size={14} className="shrink-0" /> : <ChevronRight size={14} className="shrink-0" />}
                  <span className="truncate text-sm font-semibold text-foreground">{c.party}</span>
                  <Chip tone="gray">{fmt.format(c.n_26as)} / {fmt.format(c.n_books)}</Chip>
                  {c.one_sided && (
                    <Chip tone="orange" title="Entries on one side only — this needs an alias or an explanation, not a match">
                      one-sided
                    </Chip>
                  )}
                  {/* how the names were joined is Party matching's business —
                      this row states the TRANSACTION position instead */}
                  {matchedChip(c) && <Chip tone="green">{matchedChip(c)}</Chip>}
                  {need && <Chip tone="orange" title={need.title || undefined}>{need.label}</Chip>}
                </span>
                <span className="flex flex-nowrap items-center gap-2">
                  {/* ageing: how long the oldest open entry has been open —
                      the figure that drives write-off talk */}
                  {openForDays(c.open_since, new Date()) && (
                    <span className="whitespace-nowrap text-xs text-muted-foreground">
                      {openForDays(c.open_since, new Date())}
                    </span>
                  )}
                  <Money v={c.net} bold colorize />
                </span>
              </button>
              {isOpen && (grain === 'party' ? (
                <p className="px-3 pb-3 text-xs text-muted-foreground">
                  Books carry party sums only — the figures above are the
                  party&apos;s position; there are no transactions to match.
                  Explain from the party page if the difference has a reason.
                </p>
              ) : (
                <CompanyPanel runId={runId} company={c} />
              ))}
            </div>
          )
        })}
      </div>
    </div>
  )
}
